import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCw, ShieldAlert } from "lucide-react";
import { formatDubaiDateTime } from "@/lib/dubaiDate";
import type { EntraSyncStatusResponse } from "@/types/generated/cms.generated";

interface EntraSyncStatusCardProps {
  status: EntraSyncStatusResponse | undefined;
}

const orNever = (value: string | null | undefined) => (value ? formatDubaiDateTime(value) : "Never");

/**
 * Read-only Microsoft Entra sync health for an organisation. Shown only when Entra sync is enabled.
 * When the safety catch has tripped (Microsoft returned no users, or too many removals at once), the sync removes nobody
 * until it is fixed — that is surfaced as a prominent destructive alert.
 */
export function EntraSyncStatusCard({ status }: EntraSyncStatusCardProps) {
  if (!status?.isEnabled) return null;

  return (
    <Card data-testid="entra-sync-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Microsoft sync
        </CardTitle>
        <CardDescription>
          This organisation&apos;s employees are synced from Microsoft Entra. People removed or
          disabled there are switched off here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.safetyCatchTrippedAt && (
          <Alert variant="destructive" role="alert" data-testid="entra-sync-safety-catch-alert">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Safety catch tripped</AlertTitle>
            <AlertDescription>
              On {formatDubaiDateTime(status.safetyCatchTrippedAt)} Microsoft returned no people,
              or the sync would have switched off too many at once. Nobody is switched off until
              this is fixed; new people and updates still sync.
            </AlertDescription>
          </Alert>
        )}
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-muted-foreground">Last attempt</dt>
            <dd className="font-medium" data-testid="entra-sync-last-attempt">
              {orNever(status.lastSyncAttempt)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Last success</dt>
            <dd className="font-medium" data-testid="entra-sync-last-success">
              {orNever(status.lastSuccessfulSync)}
            </dd>
          </div>
        </dl>
        {status.lastSyncError && (
          <div>
            <p className="text-sm text-muted-foreground">Last error</p>
            <p className="text-sm text-destructive break-words" data-testid="entra-sync-error">
              {status.lastSyncError}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
